// Grouping behind the contracts timeline: avenant chains, then one row per
// asset, each row packing its chains into as few sub-lanes as possible.

export interface TimelineContract {
  uid: string
  code?: string
  name?: string | null
  status?: string
  start_date?: string | null
  end_date?: string | null
  termination_date?: string | null
  contract_type_uid?: string | null
  previous_contract_uid?: string | null
  assets?: Array<{ asset_uid?: string; asset?: { uid?: string; name?: string } }> | null
}

export interface Chain {
  head: TimelineContract
  members: TimelineContract[]
}

export interface TimelineAsset {
  uid: string
  name?: string
  parent_asset_uid?: string | null
}

export interface TimelineRow {
  /** Asset uid, or null for the "No asset" group. */
  assetUid: string | null
  assetName: string
  /** Sub-lanes, each a list of non-overlapping chains in chronological order. */
  lanes: Chain[][]
}

export function parseDate(s?: string | null): number | null {
  if (!s) return null
  const t = new Date(s).getTime()
  return Number.isNaN(t) ? null : t
}

/** Drops drafts (not in force) and groups the rest into avenant chains. */
export function buildChains(contracts: TimelineContract[]): Chain[] {
  const live = contracts.filter(c => c.status !== 'draft')
  const byUid = new Map(live.map(c => [c.uid, c]))
  // A uid that is some live contract's predecessor is a chain interior/tail,
  // never a head.
  const referencedAsPrev = new Set<string>()
  live.forEach(c => {
    const p = c.previous_contract_uid
    if (p && byUid.has(p)) referencedAsPrev.add(p)
  })
  const result: Chain[] = []
  live.forEach(c => {
    if (referencedAsPrev.has(c.uid)) return // included via its successor
    const members: TimelineContract[] = []
    const guard = new Set<string>()
    let cur: TimelineContract | undefined = c
    while (cur && !guard.has(cur.uid)) {
      guard.add(cur.uid)
      members.push(cur)
      const p: string | null | undefined = cur.previous_contract_uid
      cur = p ? byUid.get(p) : undefined
    }
    // Chronological order (oldest first) so segments read left-to-right.
    members.sort((a, b) => (parseDate(a.start_date) ?? 0) - (parseDate(b.start_date) ?? 0))
    result.push({ head: c, members })
  })
  return result
}

/** [start, end] of a chain, open ends as -/+Infinity. */
function chainSpan(chain: Chain): [number, number] {
  let start = Infinity
  let end = -Infinity
  let openStart = false
  let openEnd = false
  chain.members.forEach(m => {
    const s = parseDate(m.start_date)
    const e = parseDate(m.end_date)
    if (s === null) openStart = true
    else start = Math.min(start, s)
    if (e === null) openEnd = true
    else end = Math.max(end, e)
  })
  return [openStart || start === Infinity ? -Infinity : start, openEnd || end === -Infinity ? Infinity : end]
}

/**
 * packLanes stacks chains into the fewest sub-lanes: a chain joins the first
 * sub-lane that ends before it starts, otherwise it opens a new one.
 */
export function packLanes(chains: Chain[]): Chain[][] {
  const spans = new Map(chains.map(c => [c, chainSpan(c)] as const))
  const sorted = [...chains].sort((a, b) => Math.sign(spans.get(a)![0] < spans.get(b)![0] ? -1 : spans.get(a)![0] > spans.get(b)![0] ? 1 : 0))
  const lanes: Chain[][] = []
  const laneEnds: number[] = []
  sorted.forEach(chain => {
    const [s, e] = spans.get(chain)!
    const i = laneEnds.findIndex(end => end < s)
    if (i === -1) {
      lanes.push([chain])
      laneEnds.push(e)
    } else {
      lanes[i].push(chain)
      laneEnds[i] = e
    }
  })
  return lanes
}

/**
 * buildRows puts the chains on one row per asset. A chain tied to several
 * assets appears under each, one with no asset goes in a final null-asset
 * group. Rows follow the asset tree (parent names before child names) when the
 * assets are known, and the name otherwise.
 */
export function buildRows(chains: Chain[], assets: TimelineAsset[]): TimelineRow[] {
  const known = new Map(assets.map(a => [a.uid, a]))
  const byAsset = new Map<string, { name: string; chains: Chain[] }>()
  const unassigned: Chain[] = []

  chains.forEach(chain => {
    const seen = new Set<string>()
    chain.members.forEach(m => {
      m.assets?.forEach(link => {
        const uid = link.asset_uid || link.asset?.uid
        if (!uid || seen.has(uid)) return
        seen.add(uid)
        let g = byAsset.get(uid)
        if (!g) {
          g = { name: known.get(uid)?.name || link.asset?.name || uid, chains: [] }
          byAsset.set(uid, g)
        }
        g.chains.push(chain)
      })
    })
    if (seen.size === 0) unassigned.push(chain)
  })

  // Names from the root down to the asset: comparing them gives tree order.
  const path = (uid: string, fallback: string): string[] => {
    const names: string[] = []
    const guard = new Set<string>()
    let cur = known.get(uid)
    while (cur && !guard.has(cur.uid)) {
      guard.add(cur.uid)
      names.unshift(cur.name || cur.uid)
      cur = cur.parent_asset_uid ? known.get(cur.parent_asset_uid) : undefined
    }
    return names.length ? names : [fallback]
  }
  const compare = (a: string[], b: string[]) => {
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
      const c = a[i].localeCompare(b[i])
      if (c !== 0) return c
    }
    return a.length - b.length
  }

  const rows: TimelineRow[] = [...byAsset.entries()]
    .map(([uid, g]) => ({ uid, g, key: path(uid, g.name) }))
    .sort((a, b) => compare(a.key, b.key))
    .map(({ uid, g }) => ({ assetUid: uid, assetName: g.name, lanes: packLanes(g.chains) }))

  if (unassigned.length) rows.push({ assetUid: null, assetName: '', lanes: packLanes(unassigned) })
  return rows
}
